import { SettingOutlined } from '@ant-design/icons';
import LemonIcon from '@src/components/common/image/LemonIcon';

import ActionMenu, { IActionMenuItem } from '@src/components/common/table/ActionMenu';
import { ITimunDataTableCol } from '@src/components/common/table/TimunDataTableCol';
import { classNames } from '@src/components/common/util/classNames';
import IIdRef from '@src/model/common/IdRef';
import { ICollectionDataCount } from '@src/service/business/common/types';
import AppConfigService from '@src/service/common/AppConfigService';
import LocalizeService from '@src/service/util/localize/LocalizeService';
import { Button, Checkbox, Col, Dropdown, Empty, Menu, Row, Table } from 'antd';
// tslint:disable-next-line:no-submodule-imports
import { ColumnProps, TablePaginationConfig } from 'antd/lib/table';
import { ExpandableConfig, FilterValue, SorterResult } from 'antd/lib/table/interface';
import { get } from 'lodash';
import React from 'react';

const TABLE_SCROLL_SIZE = AppConfigService.getValue('layout.table.scrollSize');

interface IVisibleColumn<T> {
  [key: string]: boolean;
}

export interface IDataTableOwnProps<T> {
  items: T[];
  columns: Array<ITimunDataTableCol<T>>;
  paginationData: ICollectionDataCount;
  paginationConfig?: Exclude<TablePaginationConfig, 'current' | 'total' | 'pageSize'>;
  className?: string;
  showActionMenu?: boolean | ((item: T) => boolean);
  actionMenuItems?: Array<IActionMenuItem<T>>;
  scrollable?: boolean;
  selectable?: boolean;
  groupActions?: Array<IActionMenuItem<T[]>>;
  emptyText?: string;
  additionalHeaderActions?: React.ReactNode;
  onSelectedChange?: (data: T[]) => void;
  expandable?: ExpandableConfig<T>;
  // onSortChange?: (key: string, order: 'ascend' | 'descend') => void; // TODO: set table sortimg string | string[]
}

type IDataTableProps<T> = IDataTableOwnProps<T>;

interface IDataTableState<T> {
  selectedElements: T[];
  visibleColumns: IVisibleColumn<T>;
  columnDropdownVisible: boolean;
  showDropdownButton: boolean;
}

class DataTable<T extends IIdRef<string>> extends React.Component<IDataTableProps<T>, IDataTableState<T>> {
  state: IDataTableState<T> = {
    selectedElements: [],
    visibleColumns: {},
    columnDropdownVisible: false,
    showDropdownButton: true,
  };

  componentDidMount = () => {
    if (this.props.columns) {
      this.setVisibleColumns();
    }
  };

  componentDidUpdate = (prevProps: IDataTableProps<T>, prevState: IDataTableState<T>) => {
    if (this.props.columns !== prevProps.columns) {
      this.setVisibleColumns();
    }
  };

  setVisibleColumns = () => {
    const visibleColumns: IVisibleColumn<T> = {};
    const allVisible = this.props.columns.every((column) => !column.columnOptional);

    this.props.columns.forEach((column) => {
      visibleColumns[column.key] = !column.columnOptional && !column.defaultHidden;
    });

    this.setState({ visibleColumns, showDropdownButton: !allVisible });
  };

  handleColumnDropdownVisibleChange = (flag: boolean) => {
    this.setState({ columnDropdownVisible: flag });
  };

  renderColumnDropdownButton = () => {
    const dropdownColumnMenu = (
      <Menu>
        {this.props.columns.map((column) => (
          <Menu.Item disabled={!column.columnOptional} onClick={() => this.toggleColumnVisible(column.key)} key={column.key.toString()}>
            <React.Fragment>
              <Checkbox checked={this.state.visibleColumns[column.key]} />
              &nbsp;
              {column.headerTitle}
            </React.Fragment>
          </Menu.Item>
        ))}
      </Menu>
    );

    return (
      <Dropdown overlay={dropdownColumnMenu} visible={this.state.columnDropdownVisible} /** dropdown is in controlled mode to disable closing on item click */ onVisibleChange={this.handleColumnDropdownVisibleChange}>
        <SettingOutlined />
      </Dropdown>
    );
  };

  getColumns = () => {
    const columns: Array<ColumnProps<T>> = this.props.columns
      .filter((column) => this.state.visibleColumns[column.key])
      .map((column) => {
        return {
          key: column.key.toString(),
          className: column.className,
          title: column.headerTitle,
          width: column.colWidth,
          render: (record: T) => (column.content ? column.content(record) : get(record, column.key)),
        };
      });
    if (this.props.showActionMenu && this.props.actionMenuItems?.length) {
      columns.push({
        key: 'actionMenu',
        width: '5%',
        render: (record: T) =>
          this.props.actionMenuItems && (
            <ActionMenu record={record} id={this.getRowKey(record)} actions={this.props.actionMenuItems}>
              <Button><LemonIcon name="ellipsis" /></Button>
            </ActionMenu>
          ),
      });
    }

    return columns;
  };

  toggleColumnVisible = (key: string) => {
    const visibleColumns = {
      ...this.state.visibleColumns,
      [key]: !this.state.visibleColumns[key],
    };

    this.setState({ visibleColumns });
  };

  render() {
    const columns = this.getColumns();
    const currentPageNumber = this.props.paginationData.pageable.pageNumber + 1;

    return (
      <React.Fragment>
        {this.state.selectedElements?.length !== 0 && (
          <Row justify="space-between" className="timun-dataTable__selectedRowsPanel">
            <Col>
              {LocalizeService.translate('DATA_TABLE.SELECTED_ROWS_COUNT_LABEL')}: {this.state.selectedElements?.length}
            </Col>
            <Col>
              {this.props.groupActions && (
                <ActionMenu record={this.state.selectedElements} id={'groupActions'} actions={this.props.groupActions}>
                  <Button type="ghost">{LocalizeService.translate('DATA_TABLE.SELECTED_ROWS_MORE_ACTIONS_LABEL')}</Button>
                </ActionMenu>
              )}
              <Button onClick={() => this.handleSelectChange([])} type="link">
                {LocalizeService.translate('COMMON.ACTION_CLEAR')}
              </Button>
            </Col>
          </Row>
        )}
        {(this.props.additionalHeaderActions || this.state.showDropdownButton) && (
          <Row justify="space-between" className="timun-dataTable__header">
            {this.props.additionalHeaderActions && <Col className="timun-dataTable__additionalAction">{this.props.additionalHeaderActions}</Col>}
            {this.state.showDropdownButton && <Col>{this.renderColumnDropdownButton()}</Col>}
          </Row>
        )}

        <Table
          className={this.getClassName()}
          columns={columns}
          dataSource={this.props.items}
          rowSelection={this.rowSelection()}
          rowKey={(record) => record.id}
          onChange={this.handleTableChange}
          scroll={this.props.scrollable ? { x: TABLE_SCROLL_SIZE } : undefined}
          locale={{
            emptyText: <Empty description={this.props.emptyText ?? LocalizeService.translate('COMMON.MESSAGE_NO_DATA')} />,
          }}
          expandable={this.props.expandable}
          pagination={{
            position: ['topRight', 'bottomRight'],
            hideOnSinglePage: true,
            showSizeChanger: false,
            total: this.props.paginationData.totalElements,
            pageSize: this.props.paginationData.size,
            current: currentPageNumber,
            responsive: true,
            ...this.props.paginationConfig,
          }}
        />
      </React.Fragment>
    );
  }

  rowSelection = () => {
    if (this.props.selectable) {
      return {
        columnWidth: '5%',
        selectedRowKeys: this.state.selectedElements.map((item: T) => this.getRowKey(item)),
        onSelect: (record: T, selected: boolean, selectedRows: any[]) => {
          this.handleSelectChange(selectedRows);
        },
        onSelectAll: (selected: boolean, selectedRows: T[]) => {
          this.handleSelectChange(selectedRows);
        },
        getCheckboxProps: (record: T) => ({
          //
        }),
      };
    }
    return undefined;
  };

  handleSelectChange = (records: T[]) => {
    this.setState({
      selectedElements: records,
    });
    if (this.props.onSelectedChange) {
      this.props.onSelectedChange(records);
    }
  };

  handleTableChange = (pagination: TablePaginationConfig, filters: Record<string, FilterValue | null>, sorter: SorterResult<T> | Array<SorterResult<T>>) => {
    if (pagination.current && pagination.pageSize && this.props.paginationConfig?.onChange) {
      this.props.paginationConfig?.onChange(pagination.current, pagination.pageSize);
    }
    // TODO: set table sortimg string | string[]
    /* if (sorter?.columnKey && typeof sorter.columnKey === 'string' && sorter?.order && this.props.onSortChange) {
      this.props.onSortChange(sorter.columnKey, sorter.order);
    } */
  };

  getClassName() {
    return classNames({
      [this.props.className || '']: true,
    });
  }

  private getRowKey = (record: T) => {
    return record.id;
  };
}

export default DataTable;
